import type {
  InternalDateTimePickerEventType,
  InternalDateTimePickerEventMap,
  DateTimePickerEventType,
  IDateTimePickerEventMap,
} from '../types/DateTimePickerEvents';

/**
 * Обработчик события
 */
export type EventHandler<T = unknown> = (event: CustomEvent<T>) => void;

/**
 * Запись подписок
 */
interface IEventSubscription {
  handler: EventHandler;
  once: boolean;
}

/**
 * Базовый класс EventEmitter для управления событиями
 */
export class EventEmitter {
  private events: Map<string, IEventSubscription[]> = new Map();

  /**
   * Подписаться на событие
   */
  on<T extends InternalDateTimePickerEventType>(
    event: T,
    handler: EventHandler<InternalDateTimePickerEventMap[T]>,
    options?: { once?: boolean },
  ): this {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }

    this.events.get(event)!.push({
      handler: handler as EventHandler,
      once: options?.once ?? false,
    });

    return this;
  }

  /**
   * Подписаться на событие один раз
   */
  once<T extends InternalDateTimePickerEventType>(
    event: T,
    handler: EventHandler<InternalDateTimePickerEventMap[T]>,
  ): this {
    return this.on(event, handler, { once: true });
  }

  /**
   * Отписаться от события
   */
  off<T extends InternalDateTimePickerEventType>(
    event: T,
    handler?: EventHandler<InternalDateTimePickerEventMap[T]>,
  ): this {
    if (!this.events.has(event)) {
      return this;
    }

    if (!handler) {
      // Удалить все обработчики для этого события
      this.events.delete(event);
      return this;
    }

    const subscriptions = this.events.get(event)!;
    const filtered = subscriptions.filter(sub => sub.handler !== handler);

    if (filtered.length === 0) {
      this.events.delete(event);
    } else {
      this.events.set(event, filtered);
    }

    return this;
  }

  /**
   * Вызвать событие
   */
  emit<T extends InternalDateTimePickerEventType>(
    event: T,
    detail: InternalDateTimePickerEventMap[T],
  ): boolean {
    if (!this.events.has(event)) {
      return false;
    }

    const subscriptions = this.events.get(event)!;
    const customEvent = new CustomEvent(event, { detail });

    // Вызываем все обработчики
    subscriptions.forEach(sub => {
      try {
        sub.handler(customEvent);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });

    // Удаляем одноразовые обработчики
    const remaining = subscriptions.filter(sub => !sub.once);
    if (remaining.length === 0) {
      this.events.delete(event);
    } else {
      this.events.set(event, remaining);
    }

    return true;
  }

  /**
   * Проверить, есть ли подписчики на событие
   */
  hasListeners(event: InternalDateTimePickerEventType): boolean {
    return this.events.has(event) && this.events.get(event)!.length > 0;
  }

  /**
   * Получить количество подписчиков на событие
   */
  listenerCount(event: InternalDateTimePickerEventType): number {
    return this.events.has(event) ? this.events.get(event)!.length : 0;
  }

  /**
   * Удалить все подписчики
   */
  removeAllListeners(event?: InternalDateTimePickerEventType): this {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
    return this;
  }
}

/**
 * Вспомогательная функция для создания CustomEvent
 */
export function createDateTimePickerEvent<T extends DateTimePickerEventType>(
  type: T,
  detail: IDateTimePickerEventMap[T],
): CustomEvent<IDateTimePickerEventMap[T]> {
  return new CustomEvent(type, { detail });
}
